import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productService } from '../services/productService';
import platformService from '../services/platformService';
import { countryOptions, getCountryCode } from '../utils/countries';

const ProductFormScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [platforms, setPlatforms] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    platformId: '',
    url: '',
    notes: '',
    country: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        const platformsData = await platformService.getActivePlatforms();
        setPlatforms(platformsData);

        if (isEditMode) {
          const productData = await productService.getProductById(id);
          if (productData) {
            setFormData({
              name: productData.name || '',
              platformId: productData.platformId || '',
              url: productData.url || '',
              notes: productData.notes || '',
              country: getCountryCode(productData.country) || '',
              city: productData.city || ''
            });
          }
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar los datos. Por favor, intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country' ? { city: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.platformId) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      setSubmitting(true);

      const productData = {
        name: formData.name,
        platformId: parseInt(formData.platformId),
        url: formData.url,
        notes: formData.notes,
        country: formData.country,
        city: formData.city
      };

      if (isEditMode) {
        await productService.updateProduct(id, productData);
      } else {
        // Aquí necesitamos implementar createProduct en el servicio
        await productService.createProduct(productData);
      }

      navigate('/products');
    } catch (err) {
      console.error('Error al guardar el producto:', err);
      setError('Error al guardar el producto: ' + (err.response?.data?.message || err.message || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">
          {isEditMode ? 'Editar Producto' : 'Nuevo Producto'}
        </h1>
        <div className="page-actions">
          <Link to="/products" className="btn btn-outline">
            Volver
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="card bg-base-100 shadow-xl">
        <form onSubmit={handleSubmit} className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Nombre del Producto</span>
              </label>
              <input
                type="text"
                name="name"
                className="input input-bordered w-full"
                placeholder="Nombre del producto"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                name="platformId"
                className="select select-bordered w-full"
                value={formData.platformId}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona una plataforma</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">País</span>
              </label>
              <select
                name="country"
                className="select select-bordered w-full"
                value={formData.country}
                onChange={handleChange}
              >
                <option value="">Sin país</option>
                {countryOptions.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Ciudad</span>
              </label>
              <input
                type="text"
                name="city"
                className="input input-bordered w-full"
                placeholder="Madrid, Roma, etc."
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">URL del Producto</span>
              </label>
              <input
                type="url"
                name="url"
                className="input input-bordered w-full"
                placeholder="https://ejemplo.com/producto"
                value={formData.url}
                onChange={handleChange}
              />
            </div>

            <div className="form-control w-full md:col-span-2">
              <label className="label">
                <span className="label-text font-medium">Notas</span>
              </label>
              <textarea
                name="notes"
                className="textarea textarea-bordered w-full h-24"
                placeholder="Notas adicionales"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="card-actions justify-end mt-6">
            <Link to="/products" className="btn btn-ghost">
              Cancelar
            </Link>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <span className="loading loading-spinner"></span>
              ) : isEditMode ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormScreen; 
